import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDriverListApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { DriverIcon } from '@hero/web-icons';
import { DriverTable } from './DriverTable';
import { ButtonCreateEntity } from '../../../app/ButtonCreateEntity';
import { TableEmptyState } from '../../../app/Table';
import { DriverCreateModal } from '../Modal';

export interface DriverTableManagerProps {
  onCreateClick?: () => void;
  hideCreateButton?: boolean;
}

export function DriverTableManager({ onCreateClick, hideCreateButton }: DriverTableManagerProps) {
  const { t } = useTranslation(['m-driver']);
  const { isEnabled: canCreateDriver } = useFeature('driver_module', 'sub_create_driver');

  const {
    data: drivers,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    runQuery,
  } = useDriverListApi({});

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      runQuery({ search: term || undefined });
    },
    [runQuery],
  );

  const handleStatusFilterChange = useCallback(
    (value: string | undefined) => {
      const next = value === statusFilter ? undefined : value;
      setStatusFilter(next);
      runQuery({ status: next });
    },
    [runQuery, statusFilter],
  );

  const handleSort = useCallback(
    (column: string) => {
      const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortColumn(column);
    },
    [sortColumn, sortDirection],
  );

  const stats = useMemo(() => {
    const counts: Record<string, number> = { active: 0, busy: 0, no_vehicle: 0 };
    for (const d of drivers) {
      if (d.status === 'active') counts['active']++;
      if (d.status === 'busy') counts['busy']++;
      if (!d.vehicle) counts['no_vehicle']++;
    }
    return counts;
  }, [drivers]);

  const statusFilterOptions = useMemo(
    () => [
      {
        value: 'active',
        label: t('status.available'),
        className: 'bg-success-50 text-success-400',
      },
      {
        value: 'busy',
        label: t('status.busy'),
        className: 'bg-warning-100 text-warning-400',
      },
      {
        value: 'no_vehicle',
        label: t('status.no_vehicle'),
        className: 'bg-gray-100 text-gray-600',
      },
    ],
    [t],
  );

  const statusFilterCounts = useMemo(
    () => [
      { value: 'active', count: stats['active'] },
      { value: 'busy', count: stats['busy'] },
      { value: 'no_vehicle', count: stats['no_vehicle'] },
    ],
    [stats],
  );

  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick();
    } else {
      setIsCreateOpen(true);
    }
  };

  const emptyState = (
    <TableEmptyState
      icon={<DriverIcon className="h-7 w-7" />}
      title={t('m-driver:table.empty.title')}
      description={t('m-driver:table.empty.description')}
      action={
        !hideCreateButton && canCreateDriver ? (
          <ButtonCreateEntity
            label={t('m-driver:table.empty.cta')}
            onClick={handleCreateClick}
          />
        ) : undefined
      }
    />
  );

  return (
    <>
      <DriverTable
        drivers={drivers}
        isLoading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        searchProps={{ term: searchTerm, setTerm: handleSearch }}
        onSort={handleSort}
        onLoadMore={onLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        emptyState={emptyState}
        hasActiveFilters={Boolean(searchTerm) || Boolean(statusFilter)}>
        <DriverTable.Head>
          <DriverTable.Search />
          <div className="flex items-center gap-3">
            <DriverTable.Badge
              options={statusFilterOptions}
              counts={statusFilterCounts}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              showCounts={false}
            />
            {!hideCreateButton && canCreateDriver && (
              <ButtonCreateEntity
                label={t('modal.create_driver_title')}
                onClick={handleCreateClick}
              />
            )}
          </div>
        </DriverTable.Head>
      </DriverTable>

      <DriverCreateModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </>
  );
}
